import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Package, TruckIcon, RotateCcw, XCircle, RefreshCw, Loader2,
  Eye, IndianRupee, AlertCircle, CheckCircle2, Store as StoreIcon,
  Calendar, PackageOpen, ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast, Toaster } from 'sonner';
import api from '@/services/api';
import { useSearchParams } from 'react-router-dom';
import { OrderDetailBody } from '@/components/hq/ecom/OrderDetailBody';
import { ORDER_STATUS, PIPELINE } from '@/components/hq/ecom/orderStatus';

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const STATUS_STYLE = ORDER_STATUS;

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { label: status, tone: 'bg-gray-100 text-gray-700' };
  return <span data-testid={`status-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.tone}`}>{s.label}</span>;
};

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <Card data-testid={testid} className="border-l-4" style={{ borderLeftColor: tone }}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ background: `${tone}15` }}>
        <Icon className="h-5 w-5" style={{ color: tone }} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
      </div>
    </CardContent>
  </Card>
);

export default function EcommerceOrdersPage() {
  const [tab, setTab] = useState('orders');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '', payment_status: '', payment_method: '',
    date_from: '', date_to: '', search: '',
  });
  const [selected, setSelected] = useState(null); // full order for the drawer
  const [detailLoading, setDetailLoading] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '', speed: 'normal', manual: false });
  const [cancelForm, setCancelForm] = useState({ reason: '', refund: true, restock: true, manual_refund_amount: '' });
  const [searchParams, setSearchParams] = useSearchParams();
  const [approveForm, setApproveForm] = useState({ schedule_pickup: true, notes: '' });
  const [rejectForm, setRejectForm] = useState({ reason: '' });
  const [busy, setBusy] = useState(false);
  const [pipelineKey, setPipelineKey] = useState('');
  const loadSeq = useRef(0);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/ecom-admin/stats');
      setStats(res.data);
    } catch (e) {
      // stats are non-critical
    }
  }, []);

  const loadOrders = useCallback(async (page = 1) => {
    setLoading(true);
    const seq = ++loadSeq.current;
    try {
      const params = { page, page_size: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/ecom-admin/orders', { params });
      if (seq !== loadSeq.current) return; // a newer filter request superseded this one
      setOrders(res.data.items || []);
      setPagination({ page: res.data.page, total_pages: res.data.total_pages, total: res.data.total });
    } catch (e) {
      toast.error('Failed to load orders', { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadReturns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/ecom-admin/returns', {
        params: { status: 'return_requested,return_approved,return_rejected,returned' },
      });
      setReturns(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load returns', { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (tab === 'orders') loadOrders(1);
    else loadReturns();
  }, [tab, loadOrders, loadReturns]);

  // Deep links from dashboard / bell: ?status=paid or ?open=<orderId>
  useEffect(() => {
    const st = searchParams.get('status');
    const open = searchParams.get('open');
    if (st) { setFilters((f) => ({ ...f, status: st })); setPipelineKey(st); }
    if (open) { openDetail(open); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openDetail = async (orderId) => {
    setDetailLoading(true);
    setSelected({ id: orderId });
    try {
      const res = await api.get(`/ecom-admin/orders/${orderId}`);
      setSelected({ ...res.data.order, _tracking: res.data.tracking });
    } catch (e) {
      toast.error('Failed to load order detail');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshCurrent = async () => {
    await loadStats();
    if (tab === 'orders') await loadOrders(pagination.page); else await loadReturns();
    if (selected?.id) {
      const res = await api.get(`/ecom-admin/orders/${selected.id}`);
      setSelected({ ...res.data.order, _tracking: res.data.tracking });
    }
  };

  const retrySR = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/ecom-admin/orders/${selected.id}/retry-shiprocket`);
      toast.success('Shiprocket push retried');
      await refreshCurrent();
    } catch (e) {
      toast.error('Retry failed', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!cancelForm.reason.trim()) return toast.error('Please provide a reason');
    setBusy(true);
    try {
      const res = await api.post(`/ecom-admin/orders/${selected.id}/cancel`, { ...cancelForm, manual_refund_amount: cancelForm.manual_refund_amount ? Number(cancelForm.manual_refund_amount) : null });
      toast.success('Order cancelled', { description: (res.data.actions || []).join(' · ') || 'Marked cancelled' });
      setCancelOpen(false);
      setCancelForm({ reason: '', refund: true });
      await refreshCurrent();
    } catch (e) {
      toast.error('Cancel failed', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const doRefund = async () => {
    if (!refundForm.reason.trim()) return toast.error('Reason is required');
    setBusy(true);
    try {
      const payload = { reason: refundForm.reason, speed: refundForm.speed, manual: !!refundForm.manual };
      if (refundForm.amount) payload.amount = parseFloat(refundForm.amount);
      await api.post(`/ecom-admin/orders/${selected.id}/refund`, payload);
      toast.success('Refund initiated');
      setRefundOpen(false);
      setRefundForm({ amount: '', reason: '', speed: 'normal' });
      await refreshCurrent();
    } catch (e) {
      toast.error('Refund failed', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const doApproveReturn = async () => {
    setBusy(true);
    try {
      await api.post(`/ecom-admin/returns/${selected.id}/approve`, approveForm);
      toast.success('Return approved');
      setApproveOpen(false);
      setApproveForm({ schedule_pickup: true, notes: '' });
      await refreshCurrent();
    } catch (e) {
      toast.error('Approve failed', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const doRejectReturn = async () => {
    if (!rejectForm.reason.trim()) return toast.error('Reason is required');
    setBusy(true);
    try {
      await api.post(`/ecom-admin/returns/${selected.id}/reject`, rejectForm);
      toast.success('Return rejected');
      setRejectOpen(false);
      setRejectForm({ reason: '' });
      await refreshCurrent();
    } catch (e) {
      toast.error('Reject failed', { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const activeRows = tab === 'orders' ? orders : returns;

  return (
    <div className="p-6 space-y-6" data-testid="ecom-admin-orders-page">
      <Toaster richColors position="top-right" />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">E-Commerce Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">Cross-store order & return management for the online store.</p>
        </div>
        <Button variant="outline" onClick={refreshCurrent} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard testid="stat-today-orders" icon={Package} label="Today's Orders" value={stats?.today_orders ?? '—'} tone="#2563eb" />
        <StatCard testid="stat-today-revenue" icon={IndianRupee} label="Today's Revenue" value={money(stats?.today_revenue || 0)} tone="#059669" />
        <StatCard testid="stat-week-orders" icon={Calendar} label="Last 7 Days" value={stats?.week_orders ?? '—'} tone="#7c3aed" />
        <StatCard testid="stat-pending-shipment" icon={TruckIcon} label="Pending Ship" value={stats?.pending_shipment ?? '—'} tone="#f59e0b" />
        <StatCard testid="stat-pending-returns" icon={RotateCcw} label="Pending Returns" value={stats?.pending_returns ?? '—'} tone="#ea580c" />
        <StatCard testid="stat-payment-failed" icon={AlertCircle} label="Payment Failed" value={stats?.payment_failed ?? '—'} tone="#dc2626" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ecom-orders-tabs">
          <TabsTrigger value="orders" data-testid="tab-orders">All Orders</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns">
            Returns {stats?.pending_returns ? (
              <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] px-1.5">{stats.pending_returns}</Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'orders' && (
        <div className="flex flex-wrap gap-1.5" data-testid="pipeline-tabs">
          {PIPELINE.map((p) => (
            <button key={p.key} type="button" onClick={() => { setFilters((f) => ({ ...f, status: p.key })); setPipelineKey(p.key); }}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${pipelineKey === p.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}
              data-testid={`pipeline-${p.key || 'all'}`}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters (orders only) */}
      {tab === 'orders' && (
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="filter-search"
                  className="pl-8"
                  placeholder="Order #, mobile, email, AWB…"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && loadOrders(1)}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_STYLE).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment</Label>
              <Select value={filters.payment_method || 'all'} onValueChange={(v) => setFilters({ ...filters, payment_method: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-payment"><SelectValue placeholder="Any method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any method</SelectItem>
                  <SelectItem value="razorpay">Razorpay (Prepaid)</SelectItem>
                  <SelectItem value="cod">Cash on Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" data-testid="filter-date-from" value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" data-testid="filter-date-to" value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div className="md:col-span-6 flex gap-2">
              <Button data-testid="apply-filters-btn" onClick={() => loadOrders(1)} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Apply
              </Button>
              <Button data-testid="clear-filters-btn" variant="ghost"
                onClick={() => setFilters({ status: '', payment_status: '', payment_method: '', date_from: '', date_to: '', search: '' })}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : activeRows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">
              <PackageOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <div className="font-medium">No {tab === 'orders' ? 'orders' : 'return requests'} yet</div>
              <div className="text-xs mt-1">They'll show up here once customers start ordering.</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Placed</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeRows.map((o) => (
                  <TableRow key={o.id} data-testid={`row-${o.id}`}>
                    <TableCell className="font-medium">{o.order_number}</TableCell>
                    <TableCell>
                      <div className="text-sm">{o.customer_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{o.customer_mobile}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        <StoreIcon className="h-3 w-3 text-muted-foreground" />
                        {o.serving_store_name || o.serving_store_id}
                      </div>
                    </TableCell>
                    <TableCell>{money(o.grand_total)}</TableCell>
                    <TableCell>
                      <div className="text-xs">{o.payment_method?.toUpperCase()}</div>
                      <div className="text-xs text-muted-foreground">{o.payment_status}</div>
                    </TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{dateTime(o.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(o.id)} data-testid={`view-${o.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {tab === 'orders' && pagination.total > 25 && (
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">
            Page {pagination.page} of {pagination.total_pages} · {pagination.total} orders
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={pagination.page <= 1}
              onClick={() => loadOrders(pagination.page - 1)} data-testid="prev-page">Prev</Button>
            <Button variant="outline" size="sm" disabled={pagination.page >= pagination.total_pages}
              onClick={() => loadOrders(pagination.page + 1)} data-testid="next-page">Next</Button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {detailLoading || !selected?.order_number ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading order…
            </div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3">
                  {selected.order_number}
                  <StatusBadge status={selected.status} />
                </SheetTitle>
                <SheetDescription>
                  Placed {dateTime(selected.created_at)} · {selected.customer_name} · {selected.customer_mobile}
                </SheetDescription>
              </SheetHeader>

              <OrderDetailBody
                order={selected}
                onChanged={(o) => { if (o) setSelected((prev) => ({ ...prev, ...o })); refreshCurrent(); }}
                onCancel={() => setCancelOpen(true)}
                onRefund={() => { setRefundForm({ amount: '', reason: '', speed: 'normal', manual: !selected.razorpay_payment_id }); setRefundOpen(true); }}
                onApproveReturn={() => setApproveOpen(true)}
                onRejectReturn={() => setRejectOpen(true)}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Order {selected?.order_number}</DialogTitle>
            <DialogDescription>
              This will mark the order as cancelled. Any Shiprocket push will be cancelled and a refund can be triggered automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason</Label>
              <Textarea data-testid="cancel-reason" placeholder="Why is this being cancelled?"
                value={cancelForm.reason} onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })} />
            </div>
            {selected?.payment_status === 'paid' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="cancel-refund-checkbox"
                  checked={cancelForm.refund} onChange={(e) => setCancelForm({ ...cancelForm, refund: e.target.checked })} />
                Auto-refund the payment via Razorpay
              </label>
            )}
            {selected?.stock_deducted && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="cancel-restock-checkbox"
                  checked={cancelForm.restock} onChange={(e) => setCancelForm({ ...cancelForm, restock: e.target.checked })} />
                Put the deducted store stock back
              </label>
            )}
            {selected?.payment_status !== 'paid' && selected?.payment_method === 'cod' && (
              <div>
                <Label>Manual refund amount (optional, COD/offline)</Label>
                <Input type="number" min="0" data-testid="cancel-manual-refund" placeholder="0.00"
                  value={cancelForm.manual_refund_amount} onChange={(e) => setCancelForm({ ...cancelForm, manual_refund_amount: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={busy}>Back</Button>
            <Button onClick={doCancel} disabled={busy} data-testid="confirm-cancel-btn" className="bg-rose-600 hover:bg-rose-700">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund {selected?.order_number}</DialogTitle>
            <DialogDescription>
              Refunds are processed through Razorpay. Leave amount blank for a full refund of the remaining balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount (₹) — optional</Label>
              <Input type="number" step="0.01" placeholder="e.g. 100 (blank = full)" data-testid="refund-amount"
                value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">
                Available to refund: {money((selected?.grand_total || 0) - (selected?.refunded_amount || 0))}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="refund-manual-checkbox" disabled={!selected?.razorpay_payment_id}
                checked={refundForm.manual} onChange={(e) => setRefundForm({ ...refundForm, manual: e.target.checked })} />
              Record as manual / offline refund (bank transfer or cash) — {selected?.razorpay_payment_id ? 'skips Razorpay' : 'required for COD'}
            </label>
            <div>
              <Label>Speed</Label>
              <Select value={refundForm.speed} onValueChange={(v) => setRefundForm({ ...refundForm, speed: v })}>
                <SelectTrigger data-testid="refund-speed"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (5–7 days)</SelectItem>
                  <SelectItem value="optimum">Optimum (instant, additional fee)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea data-testid="refund-reason" value={refundForm.reason}
                onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="Why is a refund being issued?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={busy}>Back</Button>
            <Button onClick={doRefund} disabled={busy} data-testid="confirm-refund-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Issue Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve return dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Return — {selected?.order_number}</DialogTitle>
            <DialogDescription>
              Approving will optionally schedule a Shiprocket reverse pickup from the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="approve-schedule-pickup"
                checked={approveForm.schedule_pickup}
                onChange={(e) => setApproveForm({ ...approveForm, schedule_pickup: e.target.checked })} />
              Schedule Shiprocket reverse pickup
            </label>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea data-testid="approve-notes" value={approveForm.notes}
                onChange={(e) => setApproveForm({ ...approveForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)} disabled={busy}>Back</Button>
            <Button onClick={doApproveReturn} disabled={busy} data-testid="confirm-approve-btn" className="bg-emerald-600 hover:bg-emerald-700">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject return dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Return — {selected?.order_number}</DialogTitle>
            <DialogDescription>Let the customer know why this return isn't accepted.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason</Label>
            <Textarea data-testid="reject-reason" value={rejectForm.reason}
              onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)} disabled={busy}>Back</Button>
            <Button onClick={doRejectReturn} disabled={busy} data-testid="confirm-reject-btn" variant="destructive">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
